import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Search, ArrowUpRight, CheckCircle2, XCircle, Hourglass } from "lucide-react";

const TIER_PILL = (c) => {
  if (c === "dark_green" || c === "green") return "border-good/40 bg-good-soft text-good";
  if (c === "amber") return "border-warn/40 bg-warn-soft text-warn";
  if (c === "orange" || c === "red") return "border-warn/40 bg-warn-soft text-warn";
  return "border-ink-line bg-secondary text-ink-muted";
};

export default function TrcaEpcSubmissions() {
  const [items, setItems] = useState([]);
  const [retakeReqs, setRetakeReqs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: subs }, { data: reqs }] = await Promise.all([
        api.get("/admin/trca-epc/submissions"),
        api.get("/admin/trca-epc/retake-requests"),
      ]);
      setItems(subs.items || []);
      setRetakeReqs(reqs.items || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async (id, verb) => {
    try {
      await api.post(`/admin/trca-epc/retake-requests/${id}/${verb}`);
      toast.success(`Retake request ${verb}d`);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const completed = items.filter((i) => i.status === "completed");
  const filtered = completed.filter((i) =>
    q === "" || (`${i.user_name} ${i.user_email}`).toLowerCase().includes(q.toLowerCase())
  );
  const pendingReqs = retakeReqs.filter((r) => r.status === "pending");

  return (
    <div className="px-6 py-6 space-y-6">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">Admin · Organisation Assessment</div>
          <h1 className="text-3xl font-display font-bold tracking-tight mt-1">TRCA EPC Submissions</h1>
        </div>
        <div className="text-xs text-ink-muted uppercase tracking-wider">{filtered.length} of {completed.length} shown</div>
      </header>

      {/* Pending retake requests */}
      {pendingReqs.length > 0 && (
        <div className="card-flat p-5 border-warn/40 bg-warn-soft" data-testid="trca-epc-pending-retake-section">
          <div className="flex items-center gap-2 mb-3">
            <Hourglass className="w-4 h-4 text-warn" />
            <div className="font-display font-semibold text-warn">Pending retake requests · {pendingReqs.length}</div>
          </div>
          <div className="space-y-2">
            {pendingReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-white border border-ink-line rounded-sm p-3" data-testid={`retake-req-${r.id}`}>
                <div className="text-sm">
                  <span className="font-medium">{r.user_name}</span>
                  <span className="text-ink-muted ml-2">{r.user_email}</span>
                  <span className="ml-3 font-num text-xs">Current: {r.current_score} · {r.current_tier_label}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => action(r.id, "approve")} data-testid={`retake-approve-${r.id}`}
                    className="px-3 py-1.5 text-xs bg-good text-white hover:bg-good/90 rounded-sm inline-flex items-center gap-1.5 transition-colors">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => action(r.id, "reject")} data-testid={`retake-reject-${r.id}`}
                    className="px-3 py-1.5 text-xs border border-ink-line text-warn hover:bg-warn-soft rounded-sm inline-flex items-center gap-1.5 transition-colors">
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="trca-epc-admin-search"
          className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
      </div>

      {/* Table */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-right px-4 py-2">Score</th>
              <th className="text-left px-4 py-2">Tier</th>
              <th className="text-left px-4 py-2">Completed</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-muted">No completed TRCA EPC submissions yet.</td></tr>
            ) : filtered.map((i) => (
              <tr key={i.id} className="border-t border-ink-line hover:bg-secondary/50" data-testid={`trca-epc-row-${i.id}`}>
                <td className="px-4 py-2 font-medium">{i.user_name || "—"}</td>
                <td className="px-4 py-2 text-ink-muted">{i.user_email || "—"}</td>
                <td className="px-4 py-2 text-right font-num font-bold" data-testid={`trca-epc-row-score-${i.id}`}>{i.total_score}</td>
                <td className="px-4 py-2"><span className={`chip text-[10px] ${TIER_PILL(i.tier_color)}`}>{i.tier_label || "—"}</span></td>
                <td className="px-4 py-2 text-ink-muted text-xs">{(i.completed_at || "").slice(0, 10) || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Link to={`/admin/trca-epc/${i.id}`} className="text-brand hover:underline inline-flex items-center gap-1 text-xs" data-testid={`trca-epc-view-${i.id}`}>
                    View <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
